import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ReactComponent as CheckIcon } from 'src/assets/checkbox-selected.svg';
import { ReactComponent as ChevronRightIcon } from 'src/assets/ChevronRight.svg';
import { cn } from 'src/utils/utils';

import { Icon } from './icon';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'tw:flex tw:cursor-pointer tw:select-none tw:items-center tw:gap-s tw:rounded-bw-2xl-3xl tw:px-s tw:py-s tw:text-text-primary tw:text-m tw:font-medium tw:outline-none',
      'tw:focus:bg-secondary tw:data-[state=open]:bg-secondary',
      'tw:[&_svg]:pointer-events-none tw:[&_svg]:size-l tw:[&_svg]:shrink-0',
      inset && 'tw:pl-3xl',
      className
    )}
    {...props}
  >
    {children}
    <Icon
      icon={ChevronRightIcon}
      sizeClass="tw:h-m tw:w-m"
      className="tw:ml-auto"
    />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'tw:z-1250 tw:min-w-[10rem] tw:overflow-hidden tw:rounded-bw-2xl-3xl tw:border tw:border-border-secondary tw:bg-primary tw:text-text-primary tw:shadow-e2',
      'tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out',
      'tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0',
      'tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95',
      'tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2',
      'tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2',
      'tw:origin-[--radix-dropdown-menu-content-transform-origin]',
      className
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'tw:z-1250 tw:max-h-[var(--radix-dropdown-menu-content-available-height)] tw:min-w-[10rem] tw:overflow-y-auto tw:overflow-x-hidden',
      'tw:rounded-bw-2xl-3xl tw:border tw:border-border-secondary tw:bg-primary tw:text-text-primary tw:shadow-e2',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0',
      'tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95',
      'tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2',
      'tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2',
      'tw:origin-[--radix-dropdown-menu-content-transform-origin]',
      className
    )}
    {...props}
  />
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

type DropdownMenuEdgeProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Set false to hide the divider. */
  divider?: boolean;
};

const DropdownMenuEdgeDivider = () => (
  <div className="tw:px-m">
    <div className="tw:h-px tw:w-full tw:shrink-0 tw:bg-border-secondary" />
  </div>
);

/**
 * Sticky header; render as the first child of DropdownMenuContent. Hides itself,
 * divider included, when its children render `null` (empty markup still shows).
 */
const DropdownMenuHeader = React.forwardRef<
  HTMLDivElement,
  DropdownMenuEdgeProps
>(({ className, children, divider = true, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw:sticky tw:top-0 tw:z-10 tw:w-full tw:shrink-0 tw:bg-primary',
      'tw:has-[>div:first-child:empty]:hidden',
      className
    )}
    {...props}
  >
    <div className="tw:px-m tw:py-s">{children}</div>
    {!!divider && <DropdownMenuEdgeDivider />}
  </div>
));
DropdownMenuHeader.displayName = 'DropdownMenuHeader';

/**
 * Sticky footer; render as the last child of DropdownMenuContent. Hides itself,
 * divider included, when its children render `null` (empty markup still shows).
 */
const DropdownMenuFooter = React.forwardRef<
  HTMLDivElement,
  DropdownMenuEdgeProps
>(({ className, children, divider = true, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw:sticky tw:bottom-0 tw:z-10 tw:w-full tw:shrink-0 tw:bg-primary',
      'tw:has-[>div:last-child:empty]:hidden',
      className
    )}
    {...props}
  >
    {!!divider && <DropdownMenuEdgeDivider />}
    <div className="tw:p-2xs">{children}</div>
  </div>
));
DropdownMenuFooter.displayName = 'DropdownMenuFooter';

const dropdownMenuItemVariants = cva(
  'tw:relative tw:flex tw:bg-primary tw:cursor-pointer tw:select-none tw:items-center tw:gap-s tw:rounded-2xl tw:px-s tw:py-s tw:text-m tw:font-medium tw:outline-none tw:transition-colors tw:[&_svg]:pointer-events-none tw:[&_svg]:size-l tw:[&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'tw:text-text-primary tw:hover:bg-secondary tw:focus:bg-secondary',
        destructive:
          'tw:text-text-error tw:hover:bg-error-hover tw:focus:bg-error-hover tw:[&_svg]:text-icon-error'
      },
      selected: {
        true: '',
        false: ''
      },
      disabled: {
        true: 'tw:pointer-events-none tw:bg-primary tw:text-text-disabled tw:[&_svg]:text-icon-disabled',
        false: ''
      }
    },
    compoundVariants: [
      {
        variant: 'default',
        selected: true,
        disabled: false,
        class: 'tw:bg-sec-brand-md'
      },
      {
        variant: 'destructive',
        selected: true,
        disabled: false,
        class: 'tw:bg-error-hover'
      },
      {
        variant: 'default',
        selected: true,
        disabled: true,
        class: 'tw:bg-sec-brand-light'
      },
      {
        variant: 'destructive',
        selected: true,
        disabled: true,
        class: 'tw:bg-error'
      }
    ],
    defaultVariants: {
      variant: 'default',
      selected: false,
      disabled: false
    }
  }
);

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> &
    VariantProps<typeof dropdownMenuItemVariants> & {
      inset?: boolean;
    }
>(({ className, inset, selected, disabled, variant, ...props }, ref) => (
  <div className="tw:p-2xs">
    <DropdownMenuPrimitive.Item
      ref={ref}
      disabled={!!disabled}
      className={cn(
        dropdownMenuItemVariants({
          variant,
          selected: !!selected,
          disabled: !!disabled
        }),
        inset && 'tw:pl-3xl',
        className
      )}
      {...props}
    />
  </div>
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'tw:relative tw:flex tw:cursor-pointer tw:select-none tw:items-center tw:rounded-m tw:py-s tw:pl-3xl tw:pr-s tw:text-m tw:font-medium tw:outline-none tw:transition-colors',
      'tw:text-text-primary',
      'tw:focus:bg-secondary tw:hover:bg-secondary',
      'tw:data-[disabled]:pointer-events-none tw:data-[disabled]:text-text-disabled',
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="tw:absolute tw:left-s tw:flex tw:h-l tw:w-l tw:items-center tw:justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon icon={CheckIcon} sizeClass="tw:h-m tw:w-m" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'tw:relative tw:flex tw:cursor-pointer tw:select-none tw:items-center tw:rounded-m tw:py-s tw:pl-3xl tw:pr-s tw:text-m tw:font-medium tw:outline-none tw:transition-colors',
      'tw:text-text-primary',
      'tw:focus:bg-secondary tw:hover:bg-secondary',
      'tw:data-[disabled]:pointer-events-none tw:data-[disabled]:text-text-disabled',
      className
    )}
    {...props}
  >
    <span className="tw:absolute tw:left-s tw:flex tw:h-l tw:w-l tw:items-center tw:justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <span className="tw:h-xs tw:w-xs tw:rounded-full tw:bg-brand" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'tw:px-s tw:py-s tw:text-m tw:font-bold tw:text-text-primary',
      inset && 'tw:pl-3xl',
      className
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(
      'tw:-mx-2xs tw:my-2xs tw:h-px tw:bg-border-secondary',
      className
    )}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'tw:ml-auto tw:text-s tw:tracking-widest tw:text-text-secondary',
        className
      )}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuGroup,
  DropdownMenuHeader,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
};
